import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, XCircle, DollarSign, Clock, TrendingUp, BarChart3 } from 'lucide-react';

const LinesOfCredit = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero with image */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1734503937317-3b88a42ac50c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGdyb3d0aCUyMGNoYXJ0JTIwZmluYW5jZXxlbnwwfHx8fDE3NzQ1MjQ4MTR8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-slate-900/90 to-blue-900/80" />
        </div>
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
                <Shield className="h-4 w-4 text-blue-300" />
                <span className="text-xs font-semibold text-blue-200 uppercase tracking-wider">Revolving Credit</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Lines of<br /><span className="text-blue-300">Credit</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-md">
                Up to $250,000 in unsecured revolving capital. Draw what you need, repay, and draw again without reapplying.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="loc-hero-apply" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all">
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
              </div>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {[
                { icon: <DollarSign className="h-6 w-6" />, value: '$250K', label: 'Unsecured', color: 'text-blue-300' },
                { icon: <BarChart3 className="h-6 w-6" />, value: 'Teens', label: 'Avg. Annual Rate', color: 'text-teal-300' },
                { icon: <Clock className="h-6 w-6" />, value: 'Revolving', label: 'Reuse as You Repay', color: 'text-purple-300' },
                { icon: <TrendingUp className="h-6 w-6" />, value: 'Flexible', label: 'Pay Only What You Use', color: 'text-amber-300' },
              ].map((item, i) => (
                <div key={item.label} className="bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className={`${item.color} mb-3`}>{item.icon}</div>
                  <div className="text-2xl font-bold text-white mb-0.5" style={{ fontFamily: 'var(--font-heading)' }}>{item.value}</div>
                  <div className="text-xs text-slate-400 font-medium">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-3 block">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Capital on Your Terms</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Get Approved', desc: 'Apply and receive your credit line. No collateral needed up to $250K.', icon: <CheckCircle className="h-6 w-6" /> },
              { step: '02', title: 'Draw Funds', desc: 'Access capital whenever you need it, all or part of your available line.', icon: <DollarSign className="h-6 w-6" /> },
              { step: '03', title: 'Repay & Reuse', desc: 'As you pay down, your credit replenishes. No need to reapply.', icon: <TrendingUp className="h-6 w-6" /> },
            ].map((s) => (
              <div key={s.step} className="text-center group">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-100 transition-colors">{s.icon}</div>
                <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">Step {s.step}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details Bento */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-8 md:p-10">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4 block">Program Details</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>At a Glance</h2>
              {[
                { label: 'Credit Lines', value: 'Up to $250,000 unsecured' },
                { label: 'Structure', value: 'Revolving, reuse as you repay' },
                { label: 'Interest', value: 'Pay only on drawn amounts' },
                { label: 'Avg. Annual Rate', value: 'Teens' },
                { label: 'Min. Daily Cash Balance', value: '$10,000' },
                { label: 'Time in Business', value: '2+ years' },
                { label: 'Credit Score', value: 'Equifax 675+, FICO 5' },
                { label: 'Monthly Turnover', value: '$30,000+ and growing' },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-center py-4 border-b border-slate-100 last:border-b-0">
                  <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider">{item.label}</span>
                  <span className="text-sm font-bold text-slate-900">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="bg-blue-950 text-white rounded-2xl p-8 flex-1">
                <h3 className="text-xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Eligibility Requirements</h3>
                {['Minimum daily cash balance of $10,000', '2 years in business', 'Equifax 675+, FICO 5', 'Turnover of $30,000/month and growing'].map((req) => (
                  <div key={req} className="flex items-start gap-3 mb-4 last:mb-0">
                    <CheckCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-blue-100">{req}</span>
                  </div>
                ))}
              </div>
              <div className="bg-slate-900 text-white rounded-2xl p-8">
                <h3 className="text-lg font-bold mb-5" style={{ fontFamily: 'var(--font-heading)' }}>What May Disqualify</h3>
                {['MCA loans in the past 2 months', 'MCA loans causing cash flow distress', 'Adverse credit history'].map((dis) => (
                  <div key={dis} className="flex items-start gap-3 mb-3 last:mb-0">
                    <XCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-300">{dis}</span>
                  </div>
                ))}
              </div>
              <Link to="/apply" data-testid="loc-details-apply" className="bg-blue-600 text-white rounded-2xl p-8 flex items-center justify-between group hover:bg-blue-500 transition-colors">
                <div>
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Ready to Apply?</h3>
                  <p className="text-sm text-blue-200">Get started in minutes</p>
                </div>
                <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-3 block">Use Cases</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Perfect For</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Cash Flow Gaps', desc: 'Bridge the timing gap between receivables and payables without stress.', color: 'bg-blue-50 text-blue-700' },
              { title: 'Seasonal Demand', desc: 'Stock up for busy seasons and repay as revenue comes in.', color: 'bg-teal-50 text-teal-700' },
              { title: 'Growth Opportunities', desc: 'Seize time sensitive deals, bulk inventory, new contracts, expansions.', color: 'bg-purple-50 text-purple-700' },
              { title: 'Payroll & Operations', desc: 'Keep operations running smoothly during revenue fluctuations.', color: 'bg-amber-50 text-amber-700' },
              { title: 'Emergency Funding', desc: 'Handle unexpected expenses without disrupting your business.', color: 'bg-rose-50 text-rose-700' },
              { title: 'Payment Cycles', desc: 'Bridge slow paying clients while keeping your own obligations current.', color: 'bg-indigo-50 text-indigo-700' },
            ].map((uc) => (
              <div key={uc.title} className="bento-card bg-[#FAFAFA] p-7">
                <div className={`w-10 h-10 rounded-xl ${uc.color} flex items-center justify-center mb-4`}><DollarSign className="h-5 w-5" /></div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{uc.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA with image */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/407312/pexels-photo-407312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-blue-950/90" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Flexible Capital,<br />When You Need It</h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">Stop worrying about cash flow. A revolving line of credit gives you the breathing room to focus on growth.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" data-testid="loc-final-apply" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-8 py-4 rounded-full hover:bg-slate-100 transition-colors">Apply for a Line of Credit <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LinesOfCredit;
